import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import VehicleInput from './VehicleInput';
import Recording from './Recording';
import Review from './Review';
import { uploadRecording, queueOfflineUpload, isOnline, finalizeInspection, captureDeviceInfo } from '../services/uploadService';
import { getCurrentInspection, restoreSession } from '../services/segmentService';
import { fetchInspectionById } from '../services/inspectionService';
import { warmupMicrophone, getInputDeviceName } from '../services/microphoneService';

// Dialog component
const Dialog = ({ open, onClose, title, children, actions }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface rounded-lg shadow-lg max-w-sm w-full p-6">
        <h2 className="text-lg font-semibold text-text mb-2">{title}</h2>
        <div className="text-text-muted mb-6">{children}</div>
        <div className="flex justify-end gap-3">
          {actions}
        </div>
      </div>
    </div>
  );
};

const RecordingWizard = ({ onLogout, showMessage, isAdmin = false }) => {
  const navigate = useNavigate();
  const { inspectionId: urlInspectionId } = useParams();
  const { t } = useTranslation();

  console.log('[RecordingWizard] Rendering with urlInspectionId:', urlInspectionId);

  // DEBUG: Track mount/unmount to diagnose page reload issue
  useEffect(() => {
    const mountTime = Date.now();
    console.log('[RecordingWizard] MOUNTED at', new Date().toISOString());
    return () => {
      console.log('[RecordingWizard] UNMOUNTED after', (Date.now() - mountTime) / 1000, 'seconds');
    };
  }, []);

  const [step, setStep] = useState('vehicleInput'); // vehicleInput | recording | review
  const [recordingData, setRecordingData] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ uploading: false, stage: '', progress: 0 });
  const [totalElapsedTime, setTotalElapsedTime] = useState(0); // Total time across all segments
  const [isLoadingInspection, setIsLoadingInspection] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);

  // Check for existing session on mount or restore from URL params
  useEffect(() => {
    const initializeSession = async () => {
      console.log('[RecordingWizard] initializeSession - urlInspectionId:', urlInspectionId);

      // If URL has an inspectionId, fetch from API to get latest data (including title)
      if (urlInspectionId) {
        setIsLoadingInspection(true);
        try {
          console.log('[RecordingWizard] Fetching inspection from API:', urlInspectionId);
          const inspection = await fetchInspectionById(urlInspectionId);
          console.log('[RecordingWizard] Fetched inspection:', inspection);
          // Restore session with title (title may exist even without vehicleId)
          restoreSession(urlInspectionId, inspection?.vehicleId || '', inspection?.title);
          if (inspection?.vehicleId) {
            setRecordingData(prev => ({ ...prev, vehicleId: inspection.vehicleId }));
          }
          setIsSessionReady(true);
        } catch (error) {
          console.error('[RecordingWizard] Failed to fetch inspection from URL:', error);
          // Navigate to / without inspectionId if fetch fails
          setIsSessionReady(true); // Allow fresh start
          navigate('/', { replace: true });
        } finally {
          setIsLoadingInspection(false);
        }
        return;
      }

      // No URL inspectionId - clear any stale session and start fresh
      console.log('[RecordingWizard] No URL inspectionId - clearing session for fresh start');
      localStorage.removeItem('currentInspection');
      setIsSessionReady(true);
    };

    initializeSession();
  }, [urlInspectionId, navigate]);

  // Warmup microphone on mount to avoid repeated permission prompts on iOS
  // This happens after user tap (navigate to /record), satisfying iOS user gesture requirement
  useEffect(() => {
    warmupMicrophone().catch(err => {
      console.log('Microphone warmup deferred:', err.message);
    });
  }, []);

  const handleStartRecording = (vehicleId, deviceId = null, startTriggerInfo = null) => {
    // startTriggerInfo can be { timestamp, phrase } for voice-triggered starts, or null for manual
    setRecordingData({ vehicleId, deviceId, startTriggerInfo });
    setTotalElapsedTime(0); // Reset timer each time entering recording screen
    setStep('recording');
  };

  // Handle uploaded audio file (for testing)
  const handleFileUpload = (file, vehicleId) => {
    console.log('[RecordingWizard] handleFileUpload called:', { fileName: file.name, fileType: file.type, fileSize: file.size, vehicleId });
    const recordingInfo = {
      vehicleId,
      blob: file,
      duration: 0, // Unknown for uploaded files
      mimeType: file.type || 'audio/mpeg', // Default to audio/mpeg if type is empty
    };
    console.log('[RecordingWizard] Setting recordingData and step to review');
    setRecordingData(recordingInfo);
    setStep('review'); // Go directly to review (pre-processing happens there)
  };

  const handleStopRecording = (data) => {
    // Return to vehicleInput for more segments or transcribe
    // Preserve deviceId from previous recordingData for device persistence
    // Note: IndexedDB is the source of truth for segments - no need for pendingSegment prop
    setRecordingData({
      ...data,
      deviceId: recordingData?.deviceId,
    });
    // Add segment duration to total elapsed time
    if (data.duration) {
      setTotalElapsedTime(prev => prev + data.duration);
    }
    setStep('vehicleInput');
  };

  const handleReRecord = () => {
    setStep('vehicleInput');
    setRecordingData(null);
  };

  const handleTranscribe = async (inspectionId) => {
    try {
      setUploadProgress({ uploading: true, stage: 'finalizing', progress: 0 });

      // Capture device info (with input device for microphone recordings)
      const inputDeviceName = getInputDeviceName();
      const deviceInfo = captureDeviceInfo(inputDeviceName);

      // Call finalize API endpoint to trigger concatenation and transcription
      await finalizeInspection(inspectionId, null, deviceInfo);

      // Go to review with inspectionId and vehicleId
      setRecordingData({ inspectionId, vehicleId: recordingData?.vehicleId });
      setTotalElapsedTime(0); // Reset for next session
      setStep('review');
      setUploadProgress({ uploading: false, stage: '', progress: 0 });
    } catch (error) {
      console.error('Transcribe error:', error);
      setUploadProgress({ uploading: false, stage: '', progress: 0 });

      // Show user-facing error message
      if (showMessage) {
        showMessage(error.message || 'Failed to finalize recording', 'error');
      }
    }
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelDialog(false);
    setRecordingData(null);
    setTotalElapsedTime(0); // Reset for next session
    navigate('/');
  };

  const handleBackToInput = () => {
    setStep('vehicleInput');
    setRecordingData(null);
  };

  const handleDelete = () => {
    // Called when user deletes the inspection from VehicleInput
    setRecordingData(null);
    setTotalElapsedTime(0);
  };

  const handleUpload = async (data) => {
    try {
      if (!isOnline()) {
        await queueOfflineUpload(data);
        setTimeout(() => navigate('/'), 2000);
        return { success: false, error: 'Device is offline' };
      }

      setUploadProgress({ uploading: true, stage: 'getting-url', progress: 0 });

      const result = await uploadRecording(data, (progressUpdate) => {
        setUploadProgress({
          uploading: true,
          stage: progressUpdate.stage,
          progress: progressUpdate.progress || 0,
        });
      });

      setUploadProgress({ uploading: false, stage: '', progress: 0 });

      if (result.success) {
        return result;
      } else {
        await queueOfflineUpload(data);
        setTimeout(() => navigate('/'), 3000);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Upload error:', error);
      try {
        await queueOfflineUpload(data);
      } catch (queueError) {
        console.error('Failed to queue offline:', queueError);
      }
      setUploadProgress({ uploading: false, stage: '', progress: 0 });
      return { success: false, error: error.message };
    }
  };

  // Show loading state when fetching inspection from URL or waiting for session
  if (isLoadingInspection || !isSessionReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  console.log('[RecordingWizard] Rendering with step:', step, 'recordingData:', recordingData);

  return (
    <>
      {step === 'vehicleInput' && (
        <VehicleInput
          initialVehicleId={recordingData?.vehicleId}
          onStartRecording={handleStartRecording}
          onFileUpload={handleFileUpload}
          onTranscribe={handleTranscribe}
          onCancel={() => navigate('/')}
          onBack={() => navigate('/')}
          onLogout={onLogout}
          onDelete={handleDelete}
          isAdmin={isAdmin}
        />
      )}
      {step === 'recording' && (
        <Recording
          vehicleId={recordingData?.vehicleId}
          deviceId={recordingData?.deviceId}
          startTriggerInfo={recordingData?.startTriggerInfo}
          onStopRecording={handleStopRecording}
          onCancel={handleCancel}
          onHome={() => navigate('/')}
          onLogout={onLogout}
          initialElapsedTime={totalElapsedTime}
          isAdmin={isAdmin}
        />
      )}
      {step === 'review' && (
        <Review
          recordingData={recordingData}
          onUpload={handleUpload}
          onReRecord={handleReRecord}
          onBack={handleBackToInput}
          onLogout={onLogout}
          uploadProgress={uploadProgress}
          isAdmin={isAdmin}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title={t('recording.cancel_confirm')}
        actions={
          <>
            <button
              onClick={() => setShowCancelDialog(false)}
              className="btn-secondary"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handleConfirmCancel}
              className="btn-primary bg-error hover:bg-error-dark"
            >
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {t('recording.cancel_message')}
      </Dialog>
    </>
  );
};

export default RecordingWizard;
